<template>
  <Section
    :id="id"
    :header="
      t('common.selectedParam', { param: t('shipOrders.shipOrders', 2) })
    "
    :loading="isAnyShipOrdersLoading"
    class="planning-pool-section"
  >
    <template v-if="internalShipOrders.length && !readonly" #headerRight>
      <RygenButton
        mode="tertiary"
        :icon="PrimeIcons.SEARCH"
        :label="
          t('common.findMoreParam', { param: t('shipOrders.shipOrders', 2) })
        "
        @click="isFindShipOrdersModalVisible = true"
      />
      <PlanningPoolActionButton
        :selected-ship-orders="selection"
        :proposed-shipments="proposedShipments"
        data-testid="planning-pool-action-button"
        @add-to-shipment="onBatchAddToShipment"
        @create-shipment="onBatchCreateShipment"
        @remove-from-planning-pool="onRemoveFromPlanningPool"
        @remove-from-shipment="onBatchRemoveFromShipment"
      />
    </template>

    <template #default>
      <figure
        v-if="!internalShipOrders.length && !isAnyShipOrdersLoading"
        aria-describedby="add-ship-order-instructions"
      >
        <!-- TODO: Touch this up -->
        <img
          src="@/assets/img/route_builder_empty_planning_pool.svg"
          alt=""
          width="187.5px"
          height="225px"
        />

        <figcaption>
          {{ t('common.noParamYet', { param: t('shipOrders.shipOrders', 2) }) }}
        </figcaption>

        <p id="add-ship-order-instructions">
          {{ t('routeBuilder.startWithShipOrders') }}
        </p>

        <RygenButton
          v-if="!readonly"
          mode="primary"
          :icon="PrimeIcons.PLUS"
          :label="
            t('common.addParam', { param: t('shipOrders.shipOrders', 2) })
          "
          :loading="isAnyShipOrdersLoading"
          @click="isFindShipOrdersModalVisible = true"
        />
      </figure>
      <SkeletonDataTable
        v-else
        v-model:selection="selection"
        v-model:expanded-rows="expandedRows"
        :value="internalShipOrders"
        :loading="isAnyShipOrdersLoading"
        :total-records="internalShipOrders.length"
        :collapsed-row-icon="PrimeIcons.CHEVRON_DOWN"
        :expanded-row-icon="PrimeIcons.CHEVRON_UP"
        :pt="{
          table: {
            'aria-label': t('shipOrders.shipOrders', 2),
          },
          bodyRow: ({ instance: { rowData } }: DataTablePT) => ({
            'aria-labelledby': `ship-orders-row-header-${rowData.id}`,
          }),
        }"
        data-key="id"
        class="elevated"
        table-class="p-datatable-sm"
        responsive-layout="scroll"
        scroll-height="flex"
        selection-mode="multiple"
        auto-layout
        removable-sort
        scrollable
        @row-expand="onRowExpand($event.data)"
      >
        <Column
          :reorderable-column="false"
          selection-mode="multiple"
          header-style="width: 3em"
        />

        <Column
          field="compositeId"
          :header="t('shipOrders.shipOrderNum')"
          :pt="{
            bodyCell: ({ instance: { rowData } }: ColumnPT) => ({
              id: `ship-orders-row-header-${rowData.id}`,
              role: 'rowheader',
            }),
          }"
        >
          <template #body="{ data }: DataTableRow">
            <PlanningPoolShipOrderId
              :ship-order="data"
              :proposed-shipments="proposedShipments"
              :errors="shipOrderErrors[data.id]"
            />
          </template>
        </Column>

        <Column :header="t('common.status')">
          <template #body="{ data }: DataTableRow">
            <PlanningStatusChip :status="data.planningStatus" />
          </template>
        </Column>

        <Column field="origin" :header="t('orders.origin')">
          <template #body="{ data }: DataTableRow">
            <PlanningPoolShippingPoint
              :shipping-point="data.origin"
              :ship-order="data"
            />
          </template>
        </Column>

        <Column field="destination" :header="t('orders.destination')">
          <template #body="{ data }: DataTableRow">
            <PlanningPoolShippingPoint
              :shipping-point="data.destination"
              :ship-order="data"
            />
          </template>
        </Column>

        <Column
          v-if="!readonly"
          :header-style="{ width: '3rem', 'min-width': '3rem' }"
          align-frozen="right"
          frozen
        >
          <template #body="{ data }: DataTableRow">
            <ShipOrderMenu
              :id="`ship-order-menu-${data.id}`"
              :ship-order="data"
              :proposed-shipments="proposedShipments"
              @add-to-shipment="emit('add-to-shipment', $event)"
              @create-shipment="emit('create-shipment', $event)"
              @remove-from-planning-pool="
                emit('remove-from-planning-pool', $event)
              "
              @remove-from-shipment="emit('remove-from-shipment', $event)"
              @split="onSplitShipOrder"
            />
          </template>
        </Column>

        <Column
          :header-style="{ width: '3rem', 'min-width': '3rem' }"
          align-frozen="right"
          expander
          frozen
        />

        <template #expansion="{ data }: DataTableRow">
          <RouteBuilderTableExpandedDetails
            :entity="data"
            :proposed-shipments="proposedShipments"
          />
        </template>
      </SkeletonDataTable>

      <FindUnplannedShipOrdersModal
        v-model:visible="isFindShipOrdersModalVisible"
        :planning-pool-ship-orders="shipOrders.data"
        @submit="emit('add-to-planning-pool', $event)"
      />

      <SplitShipOrderModal
        v-model:visible="splitShipOrderModal.visible"
        :ship-order="splitShipOrderModal.shipOrder"
        @submit="emit('split-ship-order', $event)"
      />

      <PlanningModal
        v-model:visible="planningModal.visible"
        :ship-orders="planningModal.shipOrders"
        @submit="onBatchCreateShipmentConfirm"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  useExpandedRows,
  useScrollAnimations,
} from '@rygen/rygen-ui/composables';
import { Loadable, Optional } from '@rygen/rygen-ui/model';

import {
  AddToPlanningPoolPayload,
  AddShipOrderToShipmentPayload,
  CreateShipmentFromShipOrdersPayload,
  ProposedShipment,
  RemoveFromPlanningPoolPayload,
  RemoveShipOrderFromShipmentPayload,
  SplitShipOrderPayload,
  ValidShipOrder,
  ScaffoldRoutePlanPayload,
} from '@/model/routeBuilder';
import { RoutePlanProposedShipment } from '@/model/routeBuilder/phase2';

import PlanningModal from '@/components/routeBuilder/planningPool/PlanningModal.vue';
import PlanningPoolActionButton from '@/components/routeBuilder/planningPool/PlanningPoolActionButton.vue';
import PlanningPoolShipOrderId from '@/components/routeBuilder/planningPool/PlanningPoolShipOrderId.vue';
import PlanningPoolShippingPoint from '@/components/routeBuilder/planningPool/PlanningPoolShippingPoint.vue';
import PlanningStatusChip from '@/components/routeBuilder/planningPool/PlanningStatusChip.vue';
import FindUnplannedShipOrdersModal from '@/components/routeBuilder/planningPool/search/FindUnplannedShipOrdersModal.vue';
import ShipOrderMenu from '@/components/routeBuilder/planningPool/ShipOrderMenu.vue';
import SplitShipOrderModal from '@/components/routeBuilder/planningPool/SplitShipOrderModal.vue';
import RouteBuilderTableExpandedDetails from '@/components/routeBuilder/RouteBuilderTableExpandedDetails.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';

export type DataTableRow = { data: ValidShipOrder; index: number };

type DataTablePT = {
  instance: { rowData: ValidShipOrder };
};

type ColumnPT = {
  instance: { rowData: ValidShipOrder };
};

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipOrders: {
    type: Object as PropType<Loadable<ValidShipOrder[]>>,
    default: () => ({ loading: false }),
  },
  proposedShipments: {
    type: Array as PropType<ProposedShipment[] | RoutePlanProposedShipment[]>,
    default: () => [],
  },
  additionalShipOrders: {
    type: Object as PropType<Loadable<ValidShipOrder[]>>,
    default: () => ({ loading: false }),
  },
  shipOrderErrors: {
    type: Object as PropType<Record<number, string[]>>,
    default: () => ({}),
  },
  // Read-only mode (viewing a committed/executed plan): hide every ship-order
  // mutation control so the panel matches the read-only shipments panel.
  readonly: {
    type: Boolean,
    default: false,
  },
});
const {
  id,
  shipOrders,
  proposedShipments,
  additionalShipOrders,
  shipOrderErrors,
  readonly,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'add-to-planning-pool', payload: AddToPlanningPoolPayload): void;
  (event: 'add-to-shipment', payload: AddShipOrderToShipmentPayload): void;
  (
    event: 'create-shipment',
    payload: CreateShipmentFromShipOrdersPayload,
  ): void;
  (
    event: 'remove-from-planning-pool',
    payload: RemoveFromPlanningPoolPayload,
  ): void;
  (
    event: 'remove-from-shipment',
    payload: RemoveShipOrderFromShipmentPayload,
  ): void;
  (event: 'scaffold', payload: ScaffoldRoutePlanPayload): void;
  (event: 'split-ship-order', payload: SplitShipOrderPayload): void;
}>();

const internalShipOrders = toRef(() => shipOrders.value.data ?? []);

const { expandedRows } = useExpandedRows(internalShipOrders, {
  expandIfSingleItem: true,
});
const { t } = useI18n();
const { scrollIntoView } = useScrollAnimations();

const isFindShipOrdersModalVisible = ref(false);
const splitShipOrderModal = ref({
  visible: false,
  shipOrder: undefined as Optional<ValidShipOrder>,
});
const planningModal = ref({
  visible: false,
  shipOrders: [] as ValidShipOrder[],
});
const selection: Ref<ValidShipOrder[]> = ref([]);

const isAnyShipOrdersLoading = computed(() => {
  return shipOrders.value.loading || additionalShipOrders.value.loading;
});

const onRowExpand = (shipOrder: ValidShipOrder) => {
  const index = internalShipOrders.value.findIndex(
    (so) => so.id === shipOrder.id,
  );
  if (index === -1) {
    console.error('Error finding shiporder', shipOrder);
    return;
  }

  nextTick(() => {
    const el = document.querySelector<HTMLElement>(
      `#ship-order-expanded-detail-${shipOrder.id}`,
    );
    scrollIntoView(el, 'center');
  });
};

const onBatchAddToShipment = (payload: AddShipOrderToShipmentPayload) => {
  emit('add-to-shipment', payload);
  selection.value = [];
};

const onBatchCreateShipment = ({
  shipOrders,
}: CreateShipmentFromShipOrdersPayload) => {
  planningModal.value.visible = true;
  planningModal.value.shipOrders = shipOrders;
};

const onBatchCreateShipmentConfirm = (
  planPayload: ScaffoldRoutePlanPayload,
) => {
  emit('scaffold', planPayload);

  selection.value = [];
};

const onRemoveFromPlanningPool = (payload: RemoveFromPlanningPoolPayload) => {
  emit('remove-from-planning-pool', payload);
  selection.value = [];
};

const onBatchRemoveFromShipment = (
  payload: RemoveShipOrderFromShipmentPayload,
) => {
  emit('remove-from-shipment', payload);
  selection.value = [];
};

const onSplitShipOrder = ({ shipOrder }: SplitShipOrderPayload) => {
  splitShipOrderModal.value.visible = true;
  splitShipOrderModal.value.shipOrder = shipOrder;
};
</script>

<style>
.planning-pool-section {
  .section-header-right {
    height: var(--input-height);
  }

  .section-data {
    display: flex;
    flex-flow: column;
    gap: var(--gap-1);
    overflow: auto;

    figure {
      display: flex;
      flex-flow: column;
      gap: var(--gap-1);
      justify-content: center;
      align-items: center;

      figcaption {
        font-weight: 700;
      }

      p {
        color: var(--p-gray-400);
      }
    }

    .p-datatable {
      position: relative;
      height: 100%;
      overflow: hidden;

      .p-datatable-table-container {
        height: 100%;
        overflow: auto;

        .p-datatable-thead {
          position: sticky;
          top: 0;
        }

        .p-datatable-tbody {
          tr:has(.error) {
            background-color: var(--p-red-50);
          }
        }
      }
    }
  }
}
</style>
